import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

import { AgentTile } from './AgentTile';
import { DIRECTION_LABELS, DIRECTION_ORDER } from '../constants';
import { useAgentInstalls } from '../queries';
import type { AgentDirection, AgentInstall } from '../types';
import { InstallStatusBadge, PrivateLock } from './AgentStatusBadge';

/**
 * Read-only roster of INSTALLED agents, grouped by direction. Visible to all
 * staff — this is the AI Team directory. Agents run via the dev13-agent skill
 * in Claude, not from here. "View" opens the standalone agent detail page.
 */
export function TeamRoster() {
  const navigate = useNavigate();
  const installsQuery = useAgentInstalls();
  const installs = useMemo(() => installsQuery.data ?? [], [installsQuery.data]);

  const openView = (install: AgentInstall) => {
    navigate(`/ai-team/${install.template_code}`);
  };

  const grouped = useMemo(() => {
    const byDirection = new Map<AgentDirection, AgentInstall[]>();
    for (const install of installs) {
      const list = byDirection.get(install.direction) ?? [];
      list.push(install);
      byDirection.set(install.direction, list);
    }
    return DIRECTION_ORDER.filter((d) => byDirection.has(d)).map((d) => ({
      direction: d,
      agents: byDirection.get(d)!,
    }));
  }, [installs]);

  if (installsQuery.isPending) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (installs.length === 0) {
    return (
      <p className="text-muted-foreground rounded-md border border-dashed p-10 text-center text-sm">
        <Bot className="mx-auto mb-2 size-6" />
        No agents on your team yet. Ask an admin to add them.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      {grouped.map(({ direction, agents }) => (
        <section key={direction} className="space-y-3">
          <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            {DIRECTION_LABELS[direction]}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {agents.map((agent) => (
              <Card
                key={agent.id}
                role="button"
                tabIndex={0}
                onClick={() => openView(agent)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openView(agent);
                  }
                }}
                className="hover:border-primary/40 hover:bg-accent/20 focus-visible:ring-ring flex cursor-pointer flex-col transition-colors focus-visible:ring-1 focus-visible:outline-none"
              >
                <CardContent className="flex flex-1 flex-col gap-2 p-4">
                  <div className="flex items-start gap-3">
                    <AgentTile
                      code={agent.template_code}
                      direction={agent.direction}
                      imageUrl={agent.image}
                      className="size-11 text-sm"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-medium">{agent.template_name}</span>
                        <PrivateLock scope={agent.access_scope} />
                      </div>
                      <div className="text-muted-foreground text-xs">
                        <code>{agent.template_code}</code>
                      </div>
                    </div>
                    <InstallStatusBadge status={agent.status} />
                  </div>
                  <Badge variant="outline" className="w-fit font-normal">
                    {DIRECTION_LABELS[agent.direction]}
                  </Badge>
                  {agent.summary ? (
                    <p className="text-muted-foreground line-clamp-2 text-sm">{agent.summary}</p>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
